<template lang="pug">
.inside-padding
  h2 Pedidos sin entregar
  p(v-if="loading" role="status") Cargando entregas…
  p(v-else-if="failed" role="alert") No se pudo cargar el reporte.
  section(v-else v-for="group in groups" :key="group.title")
    .section-heading
      h3 {{ group.title }} ({{ group.rows.length }})
      shActionButton(:action="copyGroup" :params="[group]" :disabled="!group.rows.length" icon="content_copy" text="Copiar texto" textRunning="Copiando")
      shActionButton(:action="copyImage" :params="[group]" :disabled="!group.rows.length" icon="image" text="Copiar como imagen" textRunning="Copiando imagen")
      label.selection-toggle
        input(type="checkbox" v-model="selectionMode[group.key]" @change="resetSelection(group.key)")
        span Seleccionar
        span(v-if="selectedCount(group)")  ({{ selectedCount(group) }})
      select.carrier-filter(v-model="carrierFilters[group.key]" @change="resetSelection(group.key)" aria-label="Filtrar por transportadora")
        option(value="") Todas las transportadoras
        option(v-for="carrier in group.carriers" :key="carrier.value" :value="carrier.value") {{ carrier.label }}
    .table(v-if="group.rows.length")
      table
        thead
          tr
            th Fecha de la entrega
            th Días sin entregar
            th Pedido
            th Entrega estimada
            th Ciudad
            th Transportadora
        tbody(:class="{ selectable: selectionMode[group.key] }")
          tr(v-for="(row, index) in group.rows" :key="row.delivery" :class="{ selected: selected[group.key][row.delivery] }" :aria-selected="!!selected[group.key][row.delivery]" @mousedown="startSelection($event, group, index)" @mouseenter="extendSelection($event, group, index)")
            td {{ formatOrderDate(row.createdAt) }}
            td {{ daysWithoutDelivered(row) }}
            td {{ row.order }}
            td {{ formatOrderDate(row.estimatedDeliveryAt) }}
            td.capitalize {{ row.locationRecipient || 'Sin ubicación' }}
            td.capitalize {{ row.conveyor || 'Sin asignar' }}
</template>

<script>
import gql from '@my/graphql'
import gqlDelivery from '@/graphql/delivery'
import { daysWithoutDelivered } from '@/helpers/deliveryDays'
import modals from '@my/store/modals'
import shActionButton from '@/components/elements/ActionButton'

const orderDateFormat = new Intl.DateTimeFormat('es-CO', {
  timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric'
})

export default {
  name: 'PendingDeliveries',

  components: { shActionButton },

  data () {
    return {
      rows: [],
      loading: true,
      failed: false,
      carrierFilters: { bogota: '', others: '' },
      selectionMode: { bogota: false, others: false },
      selected: { bogota: {}, others: {} },
      drag: null
    }
  },

  computed: {
    groups () {
      const bogota = { key: 'bogota', title: 'Bogotá', rows: [] }
      const others = { key: 'others', title: 'Otras ubicaciones', rows: [] }
      for (const row of this.rows) {
        const city = (row.locationRecipient || '').trim().toLowerCase()
        const group = ['bogota', 'bogotá'].includes(city) ? bogota : others
        group.rows.push(row)
      }
      return [bogota, others].map(group => {
        const carriers = Array.from(new Set(group.rows.map(row => this.carrierValue(row)))).sort()
        return {
          ...group,
          carriers: carriers.map(value => ({
            value,
            label: value === '__unassigned' ? 'Sin asignar' : value.charAt(0).toUpperCase() + value.slice(1)
          })),
          rows: group.rows.filter(row => !this.carrierFilters[group.key] || this.carrierValue(row) === this.carrierFilters[group.key])
        }
      })
    }
  },

  created () {
    this.load()
  },

  mounted () {
    window.addEventListener('mouseup', this.stopSelection)
    window.addEventListener('blur', this.stopSelection)
  },

  beforeDestroy () {
    window.removeEventListener('mouseup', this.stopSelection)
    window.removeEventListener('blur', this.stopSelection)
  },

  methods: {
    daysWithoutDelivered,
    selectedCount (group) {
      return group.rows.filter(row => this.selected[group.key][row.delivery]).length
    },
    resetSelection (key) {
      this.selected[key] = {}
      this.stopSelection()
    },
    startSelection (event, group, index) {
      if (!this.selectionMode[group.key] || event.button !== 0) return
      event.preventDefault()
      const row = group.rows[index]
      const value = !this.selected[group.key][row.delivery]
      this.drag = { key: group.key, index, value }
      this.$set(this.selected[group.key], row.delivery, value)
    },
    extendSelection (event, group, index) {
      if (!this.drag || this.drag.key !== group.key) return
      if (!(event.buttons & 1)) {
        this.stopSelection()
        return
      }
      const start = Math.min(this.drag.index, index)
      const end = Math.max(this.drag.index, index)
      for (let i = start; i <= end; i++) {
        this.$set(this.selected[group.key], group.rows[i].delivery, this.drag.value)
      }
      this.drag.index = index
    },
    stopSelection () {
      this.drag = null
    },
    carrierValue (row) {
      return (row.conveyor || '').trim().toLowerCase() || '__unassigned'
    },
    formatOrderDate (date) {
      return date ? orderDateFormat.format(new Date(date)) : 'Sin fecha'
    },
    copyRows (group) {
      const selectedRows = group.rows.filter(row => this.selected[group.key][row.delivery])
      const rows = selectedRows.length ? selectedRows : group.rows
      return [['Fecha de la entrega', 'Pedido', 'Días sin entregar']].concat(rows.map(row => [
        this.formatOrderDate(row.createdAt),
        row.order,
        String(this.daysWithoutDelivered(row))
      ]))
    },
    async copyGroup (group) {
      const text = group.title + '\n' + this.copyRows(group).map(row => row.join('\t')).join('\n')
      try {
        await navigator.clipboard.writeText(text)
      } catch (error) {
        modals.showMessage({ message: 'No se pudo copiar el reporte.', type: 'error' })
      }
    },
    async copyImage (group) {
      try {
        const rows = this.copyRows(group)
        const canvas = document.createElement('canvas')
        const width = 660
        const rowHeight = 36
        const height = 68 + rows.length * rowHeight
        canvas.width = width * 2
        canvas.height = height * 2
        const context = canvas.getContext('2d')
        context.scale(2, 2)
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, width, height)
        context.fillStyle = '#222222'
        context.font = 'bold 20px Arial'
        context.fillText(group.title, 20, 34)
        rows.forEach((row, index) => {
          const top = 52 + index * rowHeight
          context.fillStyle = index === 0 ? '#e9edf2' : (index % 2 ? '#ffffff' : '#f6f7f9')
          context.fillRect(16, top, width - 32, rowHeight)
          context.fillStyle = '#222222'
          context.font = index === 0 ? 'bold 16px Arial' : '16px Arial'
          row.forEach((value, column) => context.fillText(value, [28, 280, 430][column], top + 24))
        })
        const blob = new Promise((resolve, reject) => {
          canvas.toBlob(result => result ? resolve(result) : reject(new Error('No se pudo crear la imagen')), 'image/png')
        })
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
      } catch (error) {
        modals.showMessage({ message: 'No se pudo copiar la imagen. Revisa los permisos del portapapeles.', type: 'error' })
      }
    },
    async load () {
      this.loading = true
      this.failed = false
      try {
        const response = await gql.query(gqlDelivery.GET_PENDING_ORDERS, {}, false)
        this.rows = response.data.getPendingOrderDeliveries
        this.failed = !Array.isArray(this.rows)
      } catch (error) {
        this.failed = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
section
  margin-top 1.5rem
.section-heading
  display flex
  flex-wrap wrap
  align-items center
  gap .85rem
  h3
    margin .75rem 0
.carrier-filter
  width auto
  max-width 100%
  padding .5rem .75rem
  border 1px solid #ccc
  border-radius .3rem
  background transparent
  color inherit
  font inherit
.table
  overflow-x auto
.selection-toggle
  display inline-flex
  align-items center
  gap .35rem
  cursor pointer
  input
    width auto
    margin 0
.selectable
  user-select none
  cursor pointer
  tr:hover
    background #f0f5fa
tr.selected
  background #dcecff
table
  border-collapse collapse
  width auto
  font-size .9375rem
th, td
  text-align left
  padding .6rem .9rem
  white-space nowrap
  border-bottom 1px solid #ddd
  &:first-child
    padding-left 0
  &:nth-child(2)
    text-align right
</style>



// WEBPACK FOOTER //
// src/components/pages/delivery/PendingDeliveries.vue